import React, { useState, useMemo, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Button, Divider, List, ListItem, Paragraph, Separator, Skeleton } from '@fs/zion-ui'
import { formatValue, i18n } from '@fs/zion-locale'
import { useQueryParams } from '@fs/zion-router'
import { useGroupSearch } from 'hooks'
import { NoticeLoading } from '@fs/zion-icon'
import SearchResultsPageMiniMap, {
  SearchResultsPageMiniMapLoading,
} from '../SearchResultsPageMiniMap/SearchResultsPageMiniMap'
import trans from './PlacesWithin.trans'
import styles from './PlacesWithin.styles'

const MAX_NUMBER_PLACES_TO_SHOW = 5

function PlaceFacet({ count, name, selected, showDateRange, whereFacet, placeSelected, searchedPlace }) {
  if (!(name && searchedPlace)) {
    return null
  }

  let dateRange

  if (showDateRange) {
    const { fromYear, toYear } = whereFacet.place
    dateRange = trans.yearRange(fromYear || trans.unknownYear(), toYear || trans.presentYear())
  }

  return (
    <ListItem
      onClick={() => {
        placeSelected(whereFacet.place.id, searchedPlace.id)
      }}
      dense
      endElement={<ListItem.MetaText text={formatValue(count)} />}
      primaryText={name}
      secondaryText={dateRange}
      selected={selected}
    />
  )
}

export default function PlacesWithin({
  label,
  placeSelected,
  setMapOverlay,
  showMiniMap,
  allCountriesMode,
  miniMapRef,
  loading,
}) {
  const { place: searchedPlace, groupSearchQueryResults: searchResults, groupSearchQueryLoading } = useGroupSearch()
  const { query } = useQueryParams()
  const placesWithinId = useRef()
  const queryRef = useRef(query)
  const [miniMapLocation, setMiniMapLocation] = useState()
  const [allShown, setAllShown] = useState(false)
  const [places, setPlaces] = useState([])
  const limit = allShown ? Number.MAX_SAFE_INTEGER : MAX_NUMBER_PLACES_TO_SHOW

  useEffect(() => {
    queryRef.current = query
  }, [query])

  // Make a copy of the where facets since the data coming in from Apollo Client is immutable
  const whereFacets = useMemo(() => {
    if (searchedPlace?.id) {
      let buildWhereFacets = []

      if (searchResults?.whereFacets?.length) {
        buildWhereFacets = [...searchResults.whereFacets]
      }

      buildWhereFacets.sort((f1, f2) => f1?.place?.name?.localeCompare(f2?.place?.name, i18n.language))

      return buildWhereFacets
    }
    return []
  }, [searchResults, searchedPlace])

  const currentPlace = useMemo(() => {
    return queryRef.current.place && queryRef.current.place === searchedPlace?.id ? searchedPlace : {}
  }, [searchedPlace])

  useEffect(() => {
    if (currentPlace.latitude && currentPlace.longitude) {
      setMiniMapLocation({ latitude: currentPlace.latitude, longitude: currentPlace.longitude })
    } else {
      setMiniMapLocation(null)
    }
  }, [currentPlace, showMiniMap])

  useEffect(() => {
    const placesWithin = whereFacets.reduce((acc, facet, index) => {
      if (index < limit && facet?.place?.id && facet.place?.name) {
        acc.push(
          <PlaceFacet
            count={facet?.count}
            key={facet.place.id}
            name={facet.place.name}
            selected={currentPlace.id === Number(facet.place.id)}
            showDateRange={Boolean(
              whereFacets.find(
                (currentFacet) =>
                  currentFacet.place.id !== facet.place.id && currentFacet.place.name === facet.place.name
              )
            )}
            whereFacet={facet}
            placeSelected={placeSelected}
            searchedPlace={searchedPlace}
          />
        )
      }

      return acc
    }, [])

    const id = placesWithin
      .map((placeWithin) => placeWithin.key)
      .sort()
      .join('')

    placesWithinId.current = id
    setPlaces(placesWithin)
  }, [currentPlace, limit, whereFacets, placeSelected, searchedPlace])

  const isPlaceSearch = Boolean(searchedPlace)
  const buttonText = allShown ? trans.showLess() : trans.showAll(whereFacets.length)

  const shouldShowMap = useMemo(() => {
    if (!showMiniMap) return false

    return true
  }, [showMiniMap])

  if (loading) {
    return <PlacesWithinLoading shouldShowMap={shouldShowMap} />
  }

  return currentPlace || allCountriesMode ? (
    <>
      {isPlaceSearch && (
        <div css={styles.placeAndCount} aria-hidden="true">
          <section>{label}</section>
          <section>{groupSearchQueryLoading ? <NoticeLoading /> : formatValue(searchResults?.totalCount ?? 0)}</section>
        </div>
      )}

      {!shouldShowMap && <Separator size="sm" />}

      {groupSearchQueryLoading && (
        <>
          <Separator size="xs" />
          <SearchResultsPageMiniMapLoading />
        </>
      )}
      {shouldShowMap && !loading && !groupSearchQueryLoading && (miniMapLocation || allCountriesMode) && (
        <SearchResultsPageMiniMap
          allCountriesMode={allCountriesMode}
          currentPlace={currentPlace}
          location={miniMapLocation}
          markerCount={searchResults?.totalCount ?? 0}
          setMapOverlay={setMapOverlay}
          miniMapRef={miniMapRef}
        />
      )}

      {groupSearchQueryLoading && <PlaceFacetsLoading />}

      {!groupSearchQueryLoading && isPlaceSearch && whereFacets && (
        <>
          <Divider aria-hidden />
          <Paragraph size="xs">{trans.placesWithin(whereFacets.length)}</Paragraph>
          <List>{places}</List>
          {whereFacets.length > MAX_NUMBER_PLACES_TO_SHOW && (
            <div css={styles.showMoreContainer}>
              <Button emphasis="low" onClick={() => setAllShown(!allShown)}>
                {buttonText}
              </Button>
            </div>
          )}
        </>
      )}
    </>
  ) : null
}

PlacesWithin.propTypes = {
  label: PropTypes.string.isRequired,
  placeSelected: PropTypes.func,
}

export function PlacesWithinLoading({ shouldShowMap }) {
  return (
    <>
      {shouldShowMap && (
        <>
          <Separator size="sm" />
          <Skeleton.Paragraph size="xs" />
          <SearchResultsPageMiniMapLoading />
        </>
      )}
      <PlaceFacetsLoading />
    </>
  )
}

function PlaceFacetsLoading() {
  return (
    <>
      <Divider aria-hidden />
      <Skeleton.Paragraph size="xs" />
      <Skeleton.ListItem dense />
      <Skeleton.ListItem dense />
      <Skeleton.ListItem dense />
      <Skeleton.ListItem dense />
      <Skeleton.ListItem dense />
    </>
  )
}
